import React, { useState, useRef, useCallback } from 'react';
import { devLog, devWarn, devError } from '../../../../utils/devLogger';
import { toast } from 'react-toastify';
import GenericListUI, { FetchParams, FetchResult } from '../../../../components/CustomList/GenericListUI';
import { ColumnDefinition } from '../../../../components/CustomList/GenericDataTable';
import dayjs from 'dayjs';
import styled from 'styled-components';
import AuctioneerFormPopup, { AuctioneerFormData } from './AuctioneerFormPopup';
import LotFormPopup, { LotFormData } from './LotFormPopup';
import { getAuctioneerList, createAuctioneer, updateAuctioneer, deleteAuctioneer, createLot } from '../../../../lib/api/admin/adminApi';
import { AuctioneerData } from '../../../../lib/api/admin/adminApi.types';

// 무료 환율 API를 통한 매도환율 조회 함수
const getExchangeRateForSale = async (): Promise<number> => {
  try {
    // 여러 무료 API를 순차적으로 시도
    const apis = [
      // 1. ExchangeRate-API (가장 안정적)
      {
        name: 'ExchangeRate-API',
        url: 'https://api.exchangerate-api.com/v4/latest/USD',
        parseRate: (data: any) => data.rates.KRW
      },
      // 2. Fawaz Ahmed의 Currency API (GitHub 기반)
      {
        name: 'Currency-API',
        url: 'https://cdn.jsdelivr.net/gh/fawazahmed0/currency-api@1/latest/currencies/usd/krw.json',
        parseRate: (data: any) => data.krw
      },
      // 3. Open Exchange Rates (제한적 무료)
      {
        name: 'CurrencyAPI',
        url: 'https://api.currencyapi.com/v3/latest?apikey=cur_live_free&currencies=KRW&base_currency=USD',
        parseRate: (data: any) => data.data?.KRW?.value
      }
    ];

    for (const api of apis) {
      try {
        devLog(`${api.name} API 시도 중...`);
        const response = await fetch(api.url);
        
        if (!response.ok) {
          devWarn(`${api.name} API 응답 오류:`, response.status);
          continue;
        }
        
        const data = await response.json();
        const baseRate = api.parseRate(data);
        
        if (baseRate && typeof baseRate === 'number' && baseRate > 0) {
          // 매도환율 계산 (은행 마진 1.5% 적용)
          const sellRate = Math.round(baseRate * 1.015 * 100) / 100;
          devLog(`${api.name} 매도환율:`, sellRate);
          return sellRate;
        }
      } catch (apiError) {
        devWarn(`${api.name} API 실패:`, apiError);
        continue;
      }
    }
    
    // 모든 API 실패 시 기본값 사용
    throw new Error('모든 환율 API 호출 실패');
    
  } catch (error) {
    devError('환율 조회 실패:', error);
    
    // 기본값 반환 (2024년 기준 대략적인 USD/KRW 매도환율)
    const defaultRate = 1350;
    devWarn(`환율 조회 실패, 기본값 사용: ${defaultRate}`);
    return defaultRate;
  }
};

// 프로필 이미지 URL 생성 함수 (ProfileImageAdd와 동일한 로직)
const getProfileImageSrc = (profileImageUrl?: string | null) => {
  devLog('getProfileImageSrc 호출:', { profileImageUrl, apiHost: process.env.REACT_APP_API_HOST });
  
  if (!profileImageUrl || profileImageUrl.trim() === "") {
    return "/fluent_person.svg";
  }
  
  // 이미 완전한 URL인 경우 (http, https로 시작)
  if (profileImageUrl.startsWith("http")) {
    return profileImageUrl;
  }
  
  // 절대 경로인 경우 (/로 시작)
  if (profileImageUrl.startsWith("/")) {
    const result = `${process.env.REACT_APP_API_HOST}${profileImageUrl}`;
    devLog('절대 경로 이미지 URL 생성:', result);
    return result;
  }
  
  // 단순 파일명인 경우 (서버에서 받은 파일명)
  const result = `${process.env.REACT_APP_API_HOST}/${profileImageUrl}`;
  devLog('🔥 최종 생성된 이미지 URL:', result);
  devLog('🔥 API_HOST:', process.env.REACT_APP_API_HOST);
  devLog('🔥 파일명:', profileImageUrl);
  return result;
};

const AuctioneerManagementPage: React.FC = () => {
  devLog('AuctioneerManagementPage 렌더링 시작');
  
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const listRef = useRef<{ refetch: () => void }>(null);

  // 팝업 상태 관리
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedAuctioneer, setSelectedAuctioneer] = useState<AuctioneerData | null>(null);
  
  // LOT 팝업 상태 관리
  const [isLotPopupOpen, setIsLotPopupOpen] = useState(false);
  const [isLotEditMode, setIsLotEditMode] = useState(false);
  
  // 폼 데이터 상태
  const [formData, setFormData] = useState<AuctioneerFormData>({
    name: '',
    description: '',
    profilePic: '',
    term: '',
  });

  // LOT 폼 데이터 상태
  const [lotFormData, setLotFormData] = useState<LotFormData>({
    auctioneerName: '',
    auctioneerId: '',
    majorCategory: '',
    minorCategory: '',
    artworkName: '',
    startPriceKRW: 1000,
    thumbnailImages: [],
    artworkDetail: '',
    disabled: false,
  });

  // 폼 에러 상태
  const [formErrors, setFormErrors] = useState<Partial<AuctioneerFormData>>({});
  const [lotFormErrors, setLotFormErrors] = useState<Partial<Record<keyof LotFormData, string>>>({});

  // 폼 초기화 함수
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      profilePic: '',
      term: '',
    });
    setFormErrors({});
  };

  // 폼 데이터 유효성 검사
  const validateForm = (): boolean => {
    const errors: Partial<AuctioneerFormData> = {};

    if (!formData.name.trim()) {
      errors.name = '경매사 이름은 필수 입력 항목입니다.';
    }
    if (!formData.profilePic.trim()) {
      errors.profilePic = '로고 이미지는 필수 입력 항목입니다.';
    }
    if (!formData.term.trim()) {
      errors.term = '약관은 필수 입력 항목입니다.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 폼 입력 변경 핸들러
  const handleInputChange = (field: keyof AuctioneerFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // 해당 필드의 에러 제거
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // 이미지 업로드 핸들러
  const handleImageUpload = (file: File) => {
    // 실제 서버 업로드 로직은 여기서 구현
    devLog('이미지 파일 업로드:', file);
    // 임시로 파일명만 저장
    setFormData(prev => ({
      ...prev,
      profilePic: file.name,
    }));
  };

  // LOT 폼 초기화 함수
  const resetLotForm = () => {
    setLotFormData({
      auctioneerName: '',
      auctioneerId: '',
      majorCategory: '',
      minorCategory: '',
      artworkName: '',
      startPriceKRW: 1000,
      thumbnailImages: [],
      artworkDetail: '',
      disabled: false,
    });
    setLotFormErrors({});
  };

  // LOT 폼 데이터 유효성 검사
  const validateLotForm = (): boolean => {
    const errors: Partial<Record<keyof LotFormData, string>> = {};

    if (!lotFormData.auctioneerId) {
      errors.auctioneerId = '경매사 선택은 필수입니다.';
    }
    if (!lotFormData.majorCategory) {
      errors.majorCategory = '대분류는 필수 선택 항목입니다.';
    }
    if (!lotFormData.minorCategory) {
      errors.minorCategory = '소분류는 필수 선택 항목입니다.';
    }
    if (!lotFormData.artworkName.trim()) {
      errors.artworkName = '작품명은 필수 입력 항목입니다.';
    }
    if (!lotFormData.startPriceKRW || lotFormData.startPriceKRW < 1000) {
      errors.startPriceKRW = '시작가는 1000원 이상이어야 합니다.';
    }
    if (!lotFormData.thumbnailImages || lotFormData.thumbnailImages.length === 0) {
      errors.thumbnailImages = '최소 1장의 썸네일 이미지가 필요합니다.';
    }
    if (!lotFormData.artworkDetail.trim()) {
      errors.artworkDetail = 'LOT 설명은 필수 입력 항목입니다.';
    }

    setLotFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // LOT 폼 입력 변경 핸들러
  const handleLotInputChange = useCallback((field: keyof LotFormData, value: string | number | string[] | boolean) => {
    setLotFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // 해당 필드의 에러 제거
    setLotFormErrors(prev => ({
      ...prev,
      [field]: undefined,
    }));
  }, []);

  // LOT 이미지 업로드 핸들러
  const handleLotImageUpload = (files: File[]) => {
    devLog('LOT 이미지 파일 업로드:', files);
    // 실제 업로드 로직은 LotFormPopup에서 처리됨
  };

  // 팝업 열기 (추가 모드)
  const openAddPopup = () => {
    resetForm();
    setIsEditMode(false);
    setSelectedAuctioneer(null);
    setIsPopupOpen(true);
  };

  // 팝업 열기 (수정 모드)
  const openEditPopup = (auctioneer: AuctioneerData) => {
    setFormData({
      name: auctioneer.name,
      description: auctioneer.description || '',
      profilePic: auctioneer.profilePic || '',
      term: auctioneer.term,
    });
    setFormErrors({});
    setIsEditMode(true);
    setSelectedAuctioneer(auctioneer);
    setIsPopupOpen(true);
  };

  // 팝업 닫기
  const closePopup = () => {
    setIsPopupOpen(false);
    resetForm();
    setSelectedAuctioneer(null);
  };

  // 저장 핸들러
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (isEditMode && selectedAuctioneer) {
        // 수정 로직
        const response = await updateAuctioneer(selectedAuctioneer.index, formData);
        
        // API 응답 구조 확인: statusCode가 200이고 message가 "success"일 때만 성공
        if (response?.data?.statusCode === 200 && response?.data?.message === "success") {
          toast.success('경매사 정보가 수정되었습니다.');
          closePopup();
          setRefreshTrigger(prev => prev + 1);
          // 리스트 새로고침
          if (listRef.current) {
            listRef.current.refetch();
          }
        } else {
          // statusCode가 200이 아니거나 message가 "success"가 아닌 경우
          const errorMessage = response?.data?.error?.customMessage || 
                              response?.data?.message || 
                              '수정 처리 중 오류가 발생했습니다.';
          toast.error(`경매사 정보 수정에 실패했습니다. (${errorMessage})`);
        }
      } else {
        // 추가 로직
        const response = await createAuctioneer(formData);
        
        // API 응답 구조 확인: statusCode가 200이고 message가 "success"일 때만 성공
        if (response?.data?.statusCode === 200 && response?.data?.message === "success") {
          toast.success('새 경매사가 추가되었습니다.');
          closePopup();
          setRefreshTrigger(prev => prev + 1);
          // 리스트 새로고침
          if (listRef.current) {
            listRef.current.refetch();
          }
        } else {
          // statusCode가 200이 아니거나 message가 "success"가 아닌 경우
          const errorMessage = response?.data?.error?.customMessage || 
                              response?.data?.message || 
                              '등록 처리 중 오류가 발생했습니다.';
          toast.error(`경매사 등록에 실패했습니다. (${errorMessage})`);
        }
      }
    } catch (error: any) {
      devError('저장 중 오류:', error);
      
      // callApi에서 던진 에러 처리
      const errorMessage = error?.data?.error?.customMessage || 
                          error?.data?.message ||
                          error?.response?.data?.error?.customMessage || 
                          error?.response?.data?.message || 
                          error?.message || 
                          '네트워크 오류가 발생했습니다.';
      
      if (isEditMode) {
        toast.error(`경매사 정보 수정에 실패했습니다. (${errorMessage})`);
      } else {
        toast.error(`경매사 등록에 실패했습니다. (${errorMessage})`);
      }
    }
  };

  // 컬럼 정의
  const columns: ColumnDefinition<AuctioneerData>[] = [
    {
      header: 'No',
      accessor: 'no',
      formatter: (value) => value?.toString() || '',
    },
    {
      header: '등록일시',
      accessor: 'createAt',
      formatter: (value) => {
        if (!value) return '';
        return dayjs(value).format('YYYY.MM.DD HH:mm');
      },
    },
  {
      header: '경매사ID',
      accessor: 'code',
      formatter: (value) => value || '',
    },
    {
      header: '경매사 이름',
      accessor: 'name',
      formatter: (value) => value || '',
    },
    {
      header: '로고 이미지',
      accessor: 'profilePic',
      formatter: (value) => {
        if (!value) return '이미지 없음';
        const imageSrc = getProfileImageSrc(value);
        return (
          <ImageContainer>
            <img 
              src={imageSrc} 
              alt="로고" 
              style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.nextSibling!.textContent = '이미지 로드 실패';
              }}
            />
            <span style={{ display: 'none' }}></span>
          </ImageContainer>
        );
      },
      excelFormatter: (value) => {
        if (!value) return '이미지 없음';
        return getProfileImageSrc(value);
      },
    },
    {
      header: '소개',
      accessor: 'description',
      formatter: (value) => {
        if (!value) return '-';
        // 긴 설명은 50자로 제한하고 말줄임표 추가
        return value.length > 50 ? `${value.substring(0, 50)}...` : value;
      },
    },
    {
      header: 'LOT 등록',
      accessor: 'index',
      showColumn: false, // Excel에서 컬럼 제외
      formatter: (value, item) => (
        <LotActionButton onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          handleLotRegistration(item);
        }}>
          LOT 등록
        </LotActionButton>
      ),
    },
    {
      header: '경매사 약관',
      accessor: 'index',
      showColumn: false, // Excel에서 컬럼 제외
      formatter: (value, item) => (
        <TermsActionButton onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          handleTermsManagement(item);
        }}>
          약관 관리
        </TermsActionButton>
      ),
    },
  ];

  // 실제 API 함수
  const fetchAuctioneerData = async (params: FetchParams): Promise<FetchResult<AuctioneerData>> => {
    devLog('fetchAuctioneerData 호출됨:', params);
    
    try {
      const response = await getAuctioneerList({
        keyword: params.keyword,
        fromDate: params.fromDate,
        toDate: params.toDate,
      });

      devLog('서버 응답:', response);

      // 서버 응답 구조: { statusCode, message, data: [], metadata: { allCnt, totalCnt } }
      let data: AuctioneerData[] = [];
      let totalCount = 0;
      let allCount = 0;

      if (response?.data) {
        // 서버 응답이 { data: [], metadata: {} } 형태인 경우
        if (response.data.data && Array.isArray(response.data.data)) {
          data = response.data.data;
          // metadata가 있는 경우
          if (response.data.metadata) {
            totalCount = response.data.metadata.totalCnt || data.length;
            allCount = response.data.metadata.allCnt || data.length;
          } else {
            totalCount = data.length;
            allCount = data.length;
          }
        }
        // 예전 형태로 직접 배열인 경우 (호환성)
        else if (Array.isArray(response.data)) {
          data = response.data;
          totalCount = data.length;
          allCount = data.length;
        }
        // 다른 형태인 경우 빈 배열로 초기화
        else {
          devWarn('예상치 못한 서버 응답 구조:', response.data);
          data = [];
          totalCount = 0;
          allCount = 0;
        }
      }
      
      const result: FetchResult<AuctioneerData> = {
        data: data,
        totalItems: totalCount,
        allItems: allCount,
      };

      devLog('가공된 결과:', result);
      return result;
    } catch (error) {
      toast.error('경매사 리스트를 불러오는 중 오류가 발생했습니다.');
      return {
        data: [],
        totalItems: 0,
        allItems: 0,
      };
    }
  };

  // LOT 등록 핸들러
  const handleLotRegistration = (auctioneer: AuctioneerData) => {
    // 선택된 경매사 정보로 LOT 폼 초기화
    setLotFormData({
      auctioneerName: auctioneer.name,
      auctioneerId: auctioneer.index.toString(),
      majorCategory: '',
      minorCategory: '',
      artworkName: '',
      startPriceKRW: 1000,
      thumbnailImages: [],
      artworkDetail: '',
      disabled: false,
    });
    setLotFormErrors({});
    setIsLotEditMode(false);
    setIsLotPopupOpen(true);
  };

  // LOT 팝업 닫기
  const closeLotPopup = () => {
    setIsLotPopupOpen(false);
    resetLotForm();
  };

  // LOT 저장 핸들러
  const handleLotSave = async (processedFormData?: Partial<LotFormData>) => {
    // processedFormData가 있으면 현재 데이터에 병합
    const dataToSave = processedFormData ? { ...lotFormData, ...processedFormData } : lotFormData;
    
    if (!validateLotForm()) {
      return;
    }

    // 환율 조회 중임을 사용자에게 알림
    // toast.info('환율 정보를 조회하고 있습니다...');

    try {
      // 전날 매도환율 조회
      const exchangeRate = await getExchangeRateForSale();
      devLog('적용된 매도환율:', exchangeRate);

      // LOT 생성 API 호출용 데이터 변환
      const apiData = {
        name: dataToSave.artworkName,
        description: dataToSave.artworkDetail, // 이미지 처리가 완료된 콘텐츠
        images: dataToSave.thumbnailImages,
        startPrice: dataToSave.startPriceKRW,
        auctioneerIndex: parseInt(dataToSave.auctioneerId) || 1, // 기본값 설정
        categoryCode: dataToSave.majorCategory, // 대분류만 categoryCode로 전송
        subCategoryCode: dataToSave.minorCategory, // 소분류는 string으로 전송
        exchangeRate: exchangeRate, // 전날 매도환율 추가
        disabled: dataToSave.disabled, // LOT 활성화/비활성화 상태
      };

      devLog('LOT 생성:', apiData);
      
      // LOT 생성 API 호출
      const response = await createLot(apiData);
      
      // API 응답 구조 확인: statusCode가 200이고 message가 "success"일 때만 성공
      if (response?.data?.statusCode === 200 && response?.data?.message === "success") {
        toast.success(`LOT이 등록되었습니다. (환율: ${exchangeRate.toLocaleString()}원)`);
        closeLotPopup();
      } else {
        // statusCode가 200이 아니거나 message가 "success"가 아닌 경우
        const errorMessage = response?.data?.error?.customMessage || 
                            response?.data?.message || 
                            '등록 처리 중 오류가 발생했습니다.';
        toast.error(`LOT 등록에 실패했습니다. (${errorMessage})`);
      }
      
    } catch (error: any) {
      devError('LOT 저장 실패:', error);
      
      // callApi에서 던진 에러 처리
      const errorMessage = error?.data?.error?.customMessage || 
                          error?.data?.message ||
                          error?.response?.data?.error?.customMessage || 
                          error?.response?.data?.message || 
                          error?.message || 
                          '네트워크 오류가 발생했습니다.';
      
      toast.error(`LOT 등록에 실패했습니다. (${errorMessage})`);
      // 실패 시에는 팝업을 닫지 않음
    }
  };

  // 약관 관리 핸들러
  const handleTermsManagement = (auctioneer: AuctioneerData) => {
    setFormData({
      name: auctioneer.name,
      description: auctioneer.description || '',
      profilePic: auctioneer.profilePic || '',
      term: auctioneer.term,
    });
    setFormErrors({});
    setIsEditMode(true);
    setSelectedAuctioneer(auctioneer);
    setIsPopupOpen(true);
  };

  // 팝업에서의 삭제 핸들러 - 실제 삭제 API 호출
  const handlePopupDelete = async () => {
    if (selectedAuctioneer) {
      try {
        const response = await deleteAuctioneer(selectedAuctioneer.index);
        
        // API 응답 구조 확인: statusCode가 200이고 message가 "success"일 때만 성공
        if (response?.data?.statusCode === 200 && response?.data?.message === "success") {
          toast.success('경매사가 삭제되었습니다.');
          closePopup();
          setRefreshTrigger(prev => prev + 1);
          if (listRef.current) {
            listRef.current.refetch();
          }
        } else {
          // statusCode가 200이 아니거나 message가 "success"가 아닌 경우
          const errorMessage = response?.data?.error?.customMessage || 
                              response?.data?.message || 
                              '삭제 처리 중 오류가 발생했습니다.';
          toast.error(`경매사 삭제에 실패했습니다. (${errorMessage})`);
        }
      } catch (error: any) {
        devError('삭제 중 오류:', error);
        
        // callApi에서 던진 에러 처리
        const errorMessage = error?.data?.error?.customMessage || 
                            error?.data?.message ||
                            error?.response?.data?.error?.customMessage || 
                            error?.response?.data?.message || 
                            error?.message || 
                            '네트워크 오류가 발생했습니다.';
        
        toast.error(`경매사 삭제에 실패했습니다. (${errorMessage})`);
      }
    }
  };

  // 행 클릭 핸들러 (수정 모드로 팝업 열기)
  const handleRowClick = (item: AuctioneerData, index: number) => {
    devLog('경매사 선택:', item);
    openEditPopup(item);
  };

  // 경매사 추가 핸들러
  const handleAddAuctioneer = () => {
    openAddPopup();
  };

  return (
    <>
      {devLog('GenericListUI 렌더링 시도', { columns: columns.length, isPopupOpen })}
      <GenericListUI<AuctioneerData>
        ref={listRef}
        title="경매사 관리"
        columns={columns}
        fetchData={fetchAuctioneerData}
        excelFileName="AuctioneerList"
        keyExtractor={(item, index) => {
          return item.index ? item.index.toString() : `auctioneer-${index}`;
        }}
        enableSearch={true}
        searchPlaceholder="경매사 이름, 소개로 검색"
        enableDateFilter={true}
        dateRangeOptions={['3개월', '6개월', '1년', '지정']}
        itemsPerPageOptions={[10, 20, 50, 100]}
        themeMode="light"
        onRowClick={handleRowClick}
        refreshTrigger={refreshTrigger}
        addButton={{
          label: '경매사 추가',
          onClick: handleAddAuctioneer,
        }}
      />
      
      <AuctioneerFormPopup
        isOpen={isPopupOpen}
        isEditMode={isEditMode}
        formData={formData}
        formErrors={formErrors}
        onClose={closePopup}
        onSave={handleSave}
        onDelete={handlePopupDelete}
        onInputChange={handleInputChange}
        onImageUpload={handleImageUpload}
      />
      
      <LotFormPopup
        isOpen={isLotPopupOpen}
        isEditMode={isLotEditMode}
        formData={lotFormData}
        formErrors={lotFormErrors}
        onClose={closeLotPopup}
        onSave={handleLotSave}
        onInputChange={handleLotInputChange}
        onImageUpload={handleLotImageUpload}
        readonlyAuctioneer={true}
      />
    </>
  );
};

export default AuctioneerManagementPage;

// Styled Components
const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LotActionButton = styled.button`
  background-color: #393c53;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-right: 4px;
  
  &:hover {
    background-color: #2a2d3a;
  }
  
  &:active {
    background-color: #1f2128;
  }
`;

const TermsActionButton = styled.button`
  background-color: #000;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-right: 4px;
  
  &:hover {
    background-color: #333;
  }
  
  &:active {
    background-color: #555;
  }
`;
